import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]); // Array for 6 digits
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const navigate = useNavigate();
  const inputRefs = useRef([]);

  // Mask email: e.g., jo***@gmail.com
  const maskedEmail = email.replace(/(.{2}).*(@.*)/, "$1***$2");

  // Countdown timer - starts when OTP form is shown
  useEffect(() => {
    if (showOtpForm && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setCanResend(true);
    }
  }, [timeLeft, showOtpForm]);

  // Reset timer and OTP when switching to OTP form
  const handleSuccessfulLogin = () => {
    setShowOtpForm(true);
    setTimeLeft(60);
    setCanResend(false);
    setOtp(["", "", "", "", "", ""]);
    // Focus first OTP input
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  // Handle OTP input change
  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Only allow single digit or empty

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace to move to previous input
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste for full OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = pasted.split("").concat(Array(6 - pasted.length).fill("")).slice(0, 6);
    setOtp(newOtp);

    const nextFocus = pasted.length < 6 ? pasted.length : 5;
    inputRefs.current[nextFocus]?.focus();
  };

  // Resend OTP
  const handleResend = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Failed to resend OTP");
        return;
      }

      toast.success("OTP resent successfully!");
      setTimeLeft(60);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Login with Email & Password
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!email) {
      toast.error("Email and password are required");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Invalid email or password");
        setLoading(false);
        return;
      }

      toast.success("OTP sent successfully to your email!");
      handleSuccessfulLogin();
    } catch (err) {
      console.error("forgot password error:", err);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      toast.error("Please enter a complete 6-digit OTP");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forgot-verifyotp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: otpString }),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Invalid or expired OTP");
        setLoading(false);
        return;
      }

      toast.success("Otp verified successfully.");
      navigate("/reset-password", { state: { email } })
    } catch (err) {
      console.error("OTP verification error:", err);
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Back to login form
 

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: "100vh",
      }}
    >
      <div
        className="card shadow-lg rounded-4 p-4 p-md-5"
        style={{
          maxWidth: "450px",
          width: "100%",
          background: "#fff",
          border: "none",
        }}
      >
       {!showOtpForm && <div className="text-center mb-2">
          <h3 className="fw-bold" style={{ color: "#4c1d95" }}>
            Forgot password
          </h3>
          <p className="text-muted">Enter your registered email </p>
        </div>}

        {/* Email & Password Form */}
        {!showOtpForm ? (
        <form onSubmit={handleLoginSubmit}>
  <div className="mb-3">
    <label htmlFor="email" className="form-label fw-semibold" style={{ color: "#4c1d95" }}>
      Email Address
    </label>
    <input
      type="email"
      className="form-control rounded-3"
      id="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      required
      placeholder="admin@example.com"
      style={{ border: "1px solid #d1d5db", padding: "12px" }}
    />
  </div>

  

  <button
    type="submit"
    disabled={loading}
    className="btn w-100 text-white fw-semibold rounded-3 py-3"
    style={{
      background: "linear-gradient(90deg, #4c1d95, #6d28d9)",
      boxShadow: "0 4px 12px rgba(76, 29, 149, 0.3)",
      transition: "all 0.2s ease",
    }}
    onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = "scale(1.02)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    {loading ? "Sending OTP..." : "Send OTP"}
  </button>
</form>
        ) : (
          /* OTP Verification Form */
          <form onSubmit={handleOtpSubmit}>
            <div className="mb-2 text-center">
              <h5 className="fw-semibold" style={{ color: "#4c1d95" }}>
                Enter Verification Code
              </h5>
              <p className="text-muted mb-4">
                We've sent a 6-digit code to <strong>{maskedEmail}</strong>.<br />
                Please check your inbox or spam folder.
              </p>

              <div className="d-flex justify-content-center gap-2 mb-4" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    maxLength="1"
                    className="text-center fw-bold"
                    style={{
                      width: "50px",
                      height: "60px",
                      fontSize: "1.8rem",
                      border: "2px solid #d1d5db",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      color: "#1f2937",
                      outline: "none",
                      transition: "all 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#6d28d9")}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                    autoComplete="off"
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={loading || otp.join("").length < 6}
                className="btn w-100 text-white fw-semibold rounded-3 py-3 mb-3"
                style={{
                  background: "linear-gradient(90deg, #4c1d95, #6d28d9)",
                  boxShadow: "0 4px 12px rgba(76, 29, 149, 0.3)",
                  transition: "all 0.2s ease",
                  opacity: loading || otp.join("").length < 6 ? 0.7 : 1,
                }}
                onMouseEnter={(e) =>
                  !(loading || otp.join("").length < 6) && (e.currentTarget.style.transform = "scale(1.02)")
                }
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              <div className="text-center">
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="btn btn-link text-decoration-none fw-medium"
                    style={{ color: "#6d28d9" }}
                  >
                    Resend OTP
                  </button>
                ) : (
                  <p className="text-muted mb-2">
                    Resend code in <strong>00:{timeLeft.toString().padStart(2, "0")}</strong>
                  </p>
                )}

               
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}